import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { AurixWordmark } from "@/components/site/Logo";

const LINKS = [
  { href: "#home", label: "Home", key: "home", testid: NAV.linkHome },
  { href: "#divisions", label: "Divisions", key: "divisions", testid: NAV.linkDivisions },
  { href: "#services", label: "What We Build", key: "services", testid: NAV.linkServices },
  { href: "#portfolio", label: "Our Work", key: "portfolio", testid: NAV.linkPortfolio },
  { href: "#leadership", label: "The Builders", key: "leadership", testid: NAV.linkLeadership },
  { href: "#contact", label: "Let's Talk", key: "contact", testid: NAV.linkContact },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Track active section for nav highlighting
  useEffect(() => {
    const sections = LINKS
      .map((l) => document.querySelector(l.href))
      .filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <motion.header
        data-testid={NAV.root}
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 lg:px-10">
          <div
            className={`flex w-full items-center justify-between rounded-full px-4 py-2.5 transition-all duration-500 lg:px-6 ${
              scrolled ? "glass-strong shadow-[0_10px_40px_-15px_rgba(0,0,0,0.7)]" : ""
            }`}
          >
            <a
              href="#home"
              data-testid={NAV.logoLink}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] rounded-md"
            >
              <AurixWordmark size={34} />
            </a>

            <nav className="hidden items-center gap-1 lg:flex">
              {LINKS.map((l) => {
                const isActive = active === l.key;
                return (
                  <a
                    key={l.key}
                    href={l.href}
                    data-testid={l.testid}
                    className={`relative rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-300 ${
                      isActive
                        ? "text-[var(--gold-bright)]"
                        : "text-zinc-300 hover:text-[var(--gold-bright)]"
                    }`}
                  >
                    {l.label}
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-x-4 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent"
                      />
                    )}
                  </a>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <a
                href="#contact"
                data-testid={NAV.ctaConsult}
                className="btn-gold hidden lg:inline-flex"
                style={{ padding: "0.6rem 1.25rem", fontSize: "13px" }}
              >
                Book Strategy Call
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
              </a>
              <button
                type="button"
                data-testid={NAV.mobileToggle}
                onClick={() => setOpen((v) => !v)}
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white transition-colors hover:border-[var(--gold)]/50 lg:hidden"
                aria-label={open ? "Close menu" : "Open menu"}
              >
                {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-[76px] z-40 mx-6 rounded-2xl glass-strong lg:hidden"
          >
            <div className="flex flex-col p-4">
              {LINKS.map((l) => {
                const isActive = active === l.key;
                return (
                  <a
                    key={l.key}
                    href={l.href}
                    data-testid={`${l.testid}-mobile`}
                    onClick={() => setOpen(false)}
                    className={`rounded-lg px-4 py-3 text-[15px] font-medium transition-colors ${
                      isActive
                        ? "bg-[var(--gold)]/10 text-[var(--gold-bright)]"
                        : "text-zinc-200 hover:bg-white/5 hover:text-[var(--gold-bright)]"
                    }`}
                  >
                    {l.label}
                  </a>
                );
              })}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid={`${NAV.ctaConsult}-mobile`}
                className="btn-gold mt-3 w-full justify-center"
              >
                Book Strategy Call
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
